<template>
  <Teleport to="body">
    <div class="howto-backdrop" @click.self="$emit('close')">
      <div class="howto peek" role="dialog" aria-modal="true" :aria-label="`The ${info.label} today`" :style="{ '--role': info.colour }">
        <div class="howto__body">
          <span class="peek__kind">{{ who }}</span>
          <h3 class="peek__title">The {{ info.label }}</h3>
          <p class="peek__state">{{ state }}</p>
          <p v-if="need" class="peek__need">Asking the table for <Pip :kind="need" :n="0" text=" " /> {{ need }}</p>

          <h4 class="peek__head">Today</h4>
          <ul v-if="today.length" class="peek__acts">
            <li v-for="(a, i) in today" :key="i">
              <strong>{{ a.title }}</strong>
              <span class="peek__pips">
                <Pip v-for="p in a.paid" :key="`p${p.kind}`" :kind="p.kind" :n="-p.n" signed />
                <span v-if="a.paid.length && a.gained.length" aria-hidden="true">→</span>
                <Pip v-for="p in a.gained" :key="`g${p.kind}`" :kind="p.kind" :n="p.n" signed />
              </span>
            </li>
          </ul>
          <p v-else class="peek__none">Nothing yet today.</p>

          <h4 class="peek__head">Where they stand</h4>
          <ul class="peek__facts">
            <li v-for="(f, i) in facts" :key="i"><strong>{{ f[0] }}</strong> {{ f[1] }}</li>
          </ul>
        </div>
        <div class="howto__nav">
          <span class="howto__dots" />
          <button class="primary howto__next" @click="$emit('close')">Close</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ROLE_INFO, workersFor } from '@/engine/constants';
import { dayLog, handsOf } from '@/engine/game';
import { TECHS } from '@/engine/cards';
import { councilOf, governmentOf, GOVERNMENTS, COUNSELORS } from '@/engine/hall';
import { describeUnit, unitsOf } from '@/engine/units';
import { cityOf, BUILDINGS } from '@/engine/city';
import { loreOf } from '@/engine/lore';
import { villagesOf } from '@/engine/roleActions';
import Pip from '@/components/game/Pip.vue';

// A LOOK at another role's day (playtest 9). Seth: "click on your names on
// the left and jump into your interface for a second"; Matt: "some amount of
// peeking at each other's boards... without letting someone alpha-game the
// whole thing." So: what they DID today, what they have left, and where they
// stand in public - never a hand, a notebook, or a choice still to make.
const ORDER = ['workers', 'shifts', 'food', 'goods', 'coin', 'knowledge', 'population', 'happiness', 'threat'];
const pips = (patch) => ORDER.filter((k) => patch?.[k]).map((k) => ({ kind: k, n: patch[k] }));

export default {
  name: 'RolePeek',
  components: { Pip },
  props: {
    game: { type: Object, required: true },
    role: { type: String, required: true },
  },
  emits: ['close'],
  computed: {
    info() { return ROLE_INFO[this.role]; },
    civ() { return this.game.civ; },
    who() { return this.game.players.find((p) => p.id === this.game.roles[this.role])?.name || 'nobody yet'; },
    need() { return this.game.needs?.[this.role] || null; },
    state() {
      if (this.game.meta.phase === 'dawn') return 'Dawn: the workers are being divided.';
      if (this.game.meta.phase !== 'day') return 'The day is over.';
      if (this.game.done?.[this.role]) return 'Done for the day.';
      const n = workersFor(this.role, handsOf(this.game)[this.role] || 0);
      return `Still at work: ${n} ${n === 1 ? 'worker' : 'workers'} left.`;
    },
    today() {
      return dayLog(this.game).filter((l) => l.role === this.role).map((l) => ({
        title: l.label || l.action, paid: pips(l.paid), gained: pips(l.gained),
      }));
    },
    facts() {
      const c = this.civ;
      if (this.role === 'culture') {
        const g = governmentOf(c);
        return [
          ['Government', g ? GOVERNMENTS[g].name : 'none yet'],
          ['Council', councilOf(c).map((s) => COUNSELORS[s.id]?.name || s.id).join(', ') || 'empty'],
          ['Deck', `${(c.hall?.deck || []).length + (c.hall?.discard || []).length + (c.hall?.hand || []).length} cards`],
        ];
      }
      if (this.role === 'trade') {
        const counts = {};
        cityOf(c).buildings.forEach((b) => { counts[b.type] = (counts[b.type] || 0) + 1; });
        return [
          ['The city', Object.entries(counts).map(([t, n]) => `${n} ${(BUILDINGS[t]?.name || t).toLowerCase()}${n > 1 ? 's' : ''}`).join(', ')],
          ['Towns and villages', villagesOf(c).map((v) => v.name).join(', ') || 'none yet'],
        ];
      }
      if (this.role === 'science') {
        const lore = loreOf(c) || {};
        return [
          ['Technologies', (c.techs || []).map((t) => TECHS[t]?.name).filter(Boolean).join(', ') || 'none yet'],
          ['Theories held', `${Object.keys(lore.settled || {}).length} of 8`],
          ['Waiting on the night', `${Object.keys(lore.published || {}).length}`],
        ];
      }
      return [
        ['Units', unitsOf(c).map((u) => describeUnit(u, c)).join(', ') || 'none - the Stewards train them'],
        ['Camps in sight', `${(c.menaces || []).length}`],
        ['Raiders abroad', `${(c.raiders || []).length}`],
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.peek { border-top: 4px solid var(--role); }
.peek__kind { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--role); font-weight: 800; }
.peek__title { margin: 2px 0 4px; }
.peek__state, .peek__need { margin: 0 0 6px; }
.peek__need { font-weight: 700; display: flex; align-items: center; gap: 6px; }
.peek__head { margin: 10px 0 4px; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.7; }
.peek__acts, .peek__facts { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.peek__acts li { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.peek__pips { display: inline-flex; align-items: center; gap: 4px; }
.peek__none { margin: 0; opacity: 0.7; }
</style>
